// src/documents/exportProductionDocument.js

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import grupoLogo from '../assets/grupo_logo.png';

const PRODUCTS_COLLECTION = 'products';

// ============================================================
// FUNÇÕES AUXILIARES
// ============================================================

const loadLogo = async (workbook) => {
    try {
        const logoResponse = await fetch(grupoLogo);
        if (!logoResponse.ok) {
            throw new Error(`Falha ao carregar o logo (status ${logoResponse.status}).`);
        }
        const blob = await logoResponse.blob();

        const mime = blob.type || 'image/png';
        let ext = mime.split('/').pop().toLowerCase() || 'png';

        const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

        const supportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'];
        if (!supportedExtensions.includes(ext)) {
            throw new Error(`Formato de imagem não suportado: "${ext}".`);
        }

        if (ext === 'jpeg') ext = 'jpg';

        const logoImageId = workbook.addImage({ base64, extension: ext });

        if (logoImageId === undefined || logoImageId === null) {
            throw new Error('Logo não pôde ser adicionado à planilha.');
        }

        return logoImageId;
    } catch (error) {
        console.error('Logo loading error:', error);
        throw new Error(`Não foi possível carregar o logo: ${error.message}`);
    }
};

const applyRowBorder = (worksheet, row, colCount, style = 'medium') => {
    for (let c = 1; c <= colCount; c++) {
        const cell = worksheet.getCell(row, c);
        cell.border = {
            top: { style },
            left: { style: c === 1 ? style : 'thin' },
            bottom: { style },
            right: { style: c === colCount ? style : 'thin' }
        };
    }
};

const addTitleRow = (worksheet, row, text, colCount = 7) => {
    worksheet.mergeCells(row, 1, row, colCount);
    const cell = worksheet.getCell(row, 1);
    cell.value = text;
    cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2E4057' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(row).height = 25;
    applyRowBorder(worksheet, row, colCount, 'thin');
};

const addSectionHeader = (worksheet, row, text, colCount = 7) => {
    worksheet.mergeCells(row, 1, row, colCount);
    const cell = worksheet.getCell(row, 1);
    cell.value = text;
    cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(row).height = 25;
    applyRowBorder(worksheet, row, colCount, 'thin');
};

const sanitizeSheetName = (name) => {
    let sanitized = name.replace(/[*?:\\/[\]']/g, '');
    sanitized = sanitized.replace(/[\x00-\x1f]/g, '');
    if (sanitized.length > 31) {
        sanitized = sanitized.substring(0, 31);
    }
    return sanitized;
};

const formatDateBR = (dateValue) => {
    if (!dateValue) return '____/____/____';
    try {
        const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    } catch {
        return '____/____/____';
    }
};

// ============================================================
// ABA 1: PROCESSO DE MANIPULAÇÃO (COM COLUNA DE LOTES)
// ============================================================

const createManipulacaoSheet = (workbook, logoImageId, params) => {
    const {
        item,
        productData,
        allRawMaterials,
        allCategories,
        allItems,
        allProducts,
        allProductTypes,
        order,
        manipulationData
    } = params;

    const safeAllRawMaterials = allRawMaterials || [];
    const safeAllCategories = allCategories || [];
    const safeAllItems = allItems || [];
    const safeAllProducts = allProducts || [];
    const safeAllProductTypes = allProductTypes || [];

    const sheetName = sanitizeSheetName(`${item.productCode}_Manipulacao`);
    const worksheet = workbook.addWorksheet(sheetName);

    // Column widths - 7 colunas (adicionada coluna de Lote)
    worksheet.getColumn(1).width = 12;  // %
    worksheet.getColumn(2).width = 15;  // Código
    worksheet.getColumn(3).width = 40;  // Lote/Fornecedor (utilizado)
    worksheet.getColumn(4).width = 45;  // INCI Matéria Prima / Nome Comercial
    worksheet.getColumn(5).width = 10;  // UN
    worksheet.getColumn(6).width = 18;  // Quant.
    worksheet.getColumn(7).width = 25;  // Lote Utilizado (NOVO)

    // Calculations
    const mode = item.mode || 'mass';
    const quantity = parseFloat(item.quantity) || 0;
    const lossIndex = parseFloat(productData.lossIndex) || 1;
    const gramatura = parseFloat(productData.gramatura) || 0;
    const gramaturaUnit = productData.gramaturaUnit || 'g';

    const gramaturaBase = (gramaturaUnit === 'g' || gramaturaUnit === 'ml')
        ? gramatura / 1000
        : gramatura;

    const calcKgl = manipulationData?.calculadaKgl ? parseFloat(manipulationData.calculadaKgl) : null;
    const perdaVal = manipulationData?.perda ? parseFloat(manipulationData.perda) : null;
    const progKgl = manipulationData?.programadaKgl ? parseFloat(manipulationData.programadaKgl) : null;

    let pesoCalculado, pesoProgramado, perdaPercentual;

    if (mode === 'mass') {
        pesoCalculado = calcKgl !== null ? calcKgl : quantity;
        perdaPercentual = perdaVal !== null ? perdaVal.toString() : ((lossIndex - 1) * 100).toFixed(1);
        pesoProgramado = progKgl !== null ? progKgl : quantity * lossIndex;
    } else {
        pesoCalculado = calcKgl !== null ? calcKgl : quantity * gramaturaBase;
        perdaPercentual = perdaVal !== null ? perdaVal.toString() : ((lossIndex - 1) * 100).toFixed(1);
        pesoProgramado = progKgl !== null ? progKgl : pesoCalculado * lossIndex;
    }

    const rawMaterials = productData.rawMaterials || [];
    const preparationSteps = productData.preparationSteps || [];

    // Get packaging and serigrafia category IDs for exclusion
    const packagingCategoryIds = safeAllCategories
        .filter(c => c && ['SERIGRAFIA', 'EMBALAGEM'].includes(c.name?.toUpperCase()))
        .map(c => c.id);

    const semiFinishedCategoryIds = safeAllCategories
        .filter(c => c && ['SEMI ACABADO', 'SEMI-ACABADO'].includes(c.name?.toUpperCase()))
        .map(c => c.id);

    const semiFinishedTypeIds = safeAllProductTypes
        .filter(t => t && ['SEMI ACABADO', 'SEMI-ACABADO'].includes(t.name?.toUpperCase()))
        .map(t => t.id);

    // Build material data map
    const mpDataMap = {};
    for (const mp of rawMaterials) {
        if (!mp || !mp.id) continue;

        let productDataItem = safeAllProducts.find(p => p && p.id === mp.id);
        let isSemiFinished = false;

        if (productDataItem) {
            isSemiFinished = productDataItem.categoryIds?.some(id => semiFinishedCategoryIds.includes(id)) ||
                productDataItem.productTypeIds?.some(id => semiFinishedTypeIds.includes(id));
        }

        let itemData = safeAllItems.find(m => m && m.id === mp.id);
        let isPackaging = false;

        if (itemData) {
            isPackaging = itemData.categoryIds?.some(id => packagingCategoryIds.includes(id)) ||
                itemData.productTypeIds?.some(id => {
                    const type = safeAllProductTypes.find(t => t.id === id);
                    return type && ['SERIGRAFIA', 'EMBALAGEM'].includes(type.name?.toUpperCase());
                });
        }

        if (!itemData) {
            itemData = safeAllRawMaterials.find(m => m && m.id === mp.id);
        }

        if (!isPackaging) {
            if (isSemiFinished && productDataItem) {
                mpDataMap[mp.id] = {
                    id: productDataItem.id,
                    code: productDataItem.code || '',
                    inciName: productDataItem.inciName || '',
                    commercialName: productDataItem.commercialName || productDataItem.name || '',
                    isSemiFinished: true,
                    _quantity: mp.quantity
                };
            } else if (itemData) {
                mpDataMap[mp.id] = {
                    ...itemData,
                    isSemiFinished: false
                };
            }
        }
    }

    // Get batch selections from order
    const batchSelections = order.batchSelections || {};

    // Separate by phase
    const fases = {};
    rawMaterials.forEach(mp => {
        if (!mp || !mp.id || !mpDataMap[mp.id]) return;
        const fase = mp.fase || 'A';
        if (!fases[fase]) fases[fase] = [];
        const quantityFactor = parseFloat(mp.quantity) || 0;
        let needed = (pesoCalculado * quantityFactor) / 100;
        needed = needed * (1 + (parseFloat(perdaPercentual) || 0) / 100);

        // Get batch info for this material
        const batchInfo = batchSelections[mp.id];
        const lotNumber = batchInfo?.lotNumber || batchInfo?.batchId || '';

        fases[fase].push({
            ...mp,
            needed: needed,
            code: mpDataMap[mp.id]?.code || '',
            inciName: mp.inciName || mpDataMap[mp.id]?.inciName || '',
            commercialName: mpDataMap[mp.id]?.commercialName || '',
            isSemiFinished: mpDataMap[mp.id]?.isSemiFinished || false,
            lotNumber: lotNumber  // Adiciona o lote utilizado
        });
    });

    const faseKeys = Object.keys(fases).sort();
    let currentRow = 1;

    // ROW 1: Logo + Title + Document Code
    const row1 = worksheet.getRow(currentRow);
    row1.height = 70;

    worksheet.mergeCells('A1:B1');

    if (logoImageId != null) {
        worksheet.addImage(logoImageId, {
            tl: { col: 0.1, row: 0.050 },
            ext: { width: 160, height: 85 },
            editAs: 'oneCell'
        });
    }

    worksheet.mergeCells('C1:E1');
    worksheet.getCell('C1').value = 'Processo de Manipulação\nOrdem de Produção';
    worksheet.getCell('C1').font = { bold: true, size: 14 };
    worksheet.getCell('C1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    worksheet.getCell('F1').value = 'FQ 05\nRev. 04';
    worksheet.getCell('F1').font = { bold: true, size: 12 };
    worksheet.getCell('F1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    worksheet.mergeCells('G1:G1');
    worksheet.getCell('G1').value = '';
    worksheet.getCell('G1').alignment = { horizontal: 'center', vertical: 'middle' };

    ['A', 'B', 'C', 'D', 'E', 'F', 'G'].forEach(col => {
        worksheet.getCell(`${col}1`).border = {
            top: { style: 'medium' }, left: { style: 'medium' },
            bottom: { style: 'thin' }, right: { style: 'medium' }
        };
    });

    // Row 2: Checkboxes
    currentRow = 2;
    const row2 = worksheet.getRow(currentRow);
    row2.height = 30;

    worksheet.mergeCells('A2:B2');
    worksheet.getCell('A2').value = '☐ Desenvolvimento Aprovado';
    worksheet.mergeCells('C2:D2');
    worksheet.getCell('C2').value = '☐ Desenvolvimento Reprovado';
    worksheet.mergeCells('E2:G2');
    worksheet.getCell('E2').value = '☐ Produção';

    ['A2', 'C2', 'E2'].forEach(cellRef => {
        const cell = worksheet.getCell(cellRef);
        cell.font = { size: 11 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    for (let c = 1; c <= 7; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' }
        };
    }

    // Row 3: Date and Responsible
    currentRow = 3;
    const row3 = worksheet.getRow(currentRow);
    row3.height = 25;

    worksheet.mergeCells('A3:C3');
    const fabDate = order.fabricationDate ? formatDateBR(order.fabricationDate) : '____/____/____';
    worksheet.getCell('A3').value = `Data: ${fabDate}`;
    worksheet.mergeCells('D3:G3');
    worksheet.getCell('D3').value = 'Visto Responsável:';

    ['A3', 'D3'].forEach(cellRef => {
        const cell = worksheet.getCell(cellRef);
        cell.font = { size: 11 };
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
    });

    for (let c = 1; c <= 7; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' }
        };
    }

    // Row 4: Section title
    currentRow = 4;
    addSectionHeader(worksheet, currentRow, 'INFORMAÇÕES SOBRE O PRODUTO FABRICADO', 7);
    currentRow++;

    // Row 5: Calculated KG/L, Loss, Programmed KG/L
    const row5 = worksheet.getRow(currentRow);
    row5.height = 25;
    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    const calcDisplay = Number(pesoCalculado);
    worksheet.getCell(`A${currentRow}`).value = `CALCULADA KG/L: ${calcDisplay.toFixed(3)}`;

    worksheet.getCell(`D${currentRow}`).value = `PERDA: ${perdaPercentual}%`;

    worksheet.mergeCells(`E${currentRow}:G${currentRow}`);
    const progDisplay = Number(pesoProgramado);
    worksheet.getCell(`E${currentRow}`).value = `PROGRAMADA KG/L: ${progDisplay.toFixed(3)}`;

    for (let c = 1; c <= 7; c++) {
        const cell = worksheet.getCell(currentRow, c);
        cell.font = { bold: true, size: 11 };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Row 6: Product and Lot
    const row6 = worksheet.getRow(currentRow);
    row6.height = 25;

    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    const mergedCellA = worksheet.getCell(`A${currentRow}`);
    mergedCellA.value = `PRODUTO: ${item.productCode} - ${item.productName}`;
    mergedCellA.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    mergedCellA.font = { bold: true, size: 11 };
    mergedCellA.border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
    };

    worksheet.mergeCells(`D${currentRow}:G${currentRow}`);
    const mergedCellD = worksheet.getCell(`D${currentRow}`);
    mergedCellD.value = `LOTE: ${item.lote || '___________'}`;
    mergedCellD.alignment = { horizontal: 'left', vertical: 'middle' };
    mergedCellD.font = { bold: true, size: 11 };
    mergedCellD.border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
    };
    currentRow++;

    // Row 7: Tank, Temp Ambiente, OP
    const row7 = worksheet.getRow(currentRow);
    row7.height = 25;

    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'TANQUE: ';
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.getCell(`A${currentRow}`).font = { size: 11 };
    worksheet.getCell(`A${currentRow}`).border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
    };

    const cellD7 = worksheet.getCell(`D${currentRow}`);
    cellD7.value = 'TEMPERATURA AMBIENTE: ';
    cellD7.alignment = { horizontal: 'left', vertical: 'middle' };
    cellD7.font = { size: 11 };
    cellD7.border = {
        top: { style: 'thin' },
        left: { style: 'medium' }
    };

    worksheet.mergeCells(`E${currentRow}:G${currentRow}`);
    const mergedCellE7 = worksheet.getCell(`E${currentRow}`);
    mergedCellE7.value = `OP: ${order.opCode || '___________'}`;
    mergedCellE7.alignment = { horizontal: 'left', vertical: 'middle' };
    mergedCellE7.font = { size: 11 };
    mergedCellE7.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'medium' }
    };
    currentRow++;

    // Row 8: Manufacturing Date and Expiry
    const row8 = worksheet.getRow(currentRow);
    row8.height = 25;

    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    const fabDateDisplay = order.fabricationDate ? formatDateBR(order.fabricationDate) : '____/____/____';
    worksheet.getCell(`A${currentRow}`).value = `DATA DE FABRICAÇÃO: ${fabDateDisplay}`;
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.getCell(`A${currentRow}`).font = { size: 11 };
    worksheet.getCell(`A${currentRow}`).border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
    };

    worksheet.mergeCells(`D${currentRow}:G${currentRow}`);
    const expDateDisplay = order.expirationDate ? formatDateBR(order.expirationDate) : '____/____/____';
    worksheet.getCell(`D${currentRow}`).value = `DATA DE VALIDADE: ${expDateDisplay}`;
    worksheet.getCell(`D${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.getCell(`D${currentRow}`).font = { size: 11 };
    worksheet.getCell(`D${currentRow}`).border = {
        top: { style: 'thin' },
        left: { style: 'medium' },
        right: { style: 'medium' }
    };
    currentRow++;

    // Row 9: Manipulacao Schedules
    const row9 = worksheet.getRow(currentRow);
    row9.height = 25;
    worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'HORÁRIO ÍNICIO MANIPULAÇÃO:  ____:____:____';

    worksheet.mergeCells(`D${currentRow}:G${currentRow}`);
    worksheet.getCell(`D${currentRow}`).value = 'HORÁRIO FIM MANIPULAÇÃO:  ____:____:____';

    worksheet.getCell(`A${currentRow}`).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    worksheet.getCell(`D${currentRow}`).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    currentRow++;

    // Row 10: Table headers (com coluna de Lote Utilizado)
    const headerRow = worksheet.getRow(currentRow);
    headerRow.height = 30;
    const headers = ['%', 'Código', 'Lote/Fornecedor', 'INCI Matéria Prima / Nome Comercial', 'UN', 'Quant.', 'Lote Utilizado'];
    headers.forEach((header, i) => {
        const cell = worksheet.getCell(currentRow, i + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3A5C' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    });
    for (let c = 1; c <= 7; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    }
    currentRow++;

    const itemsStartRow = currentRow;

    // Material rows by phase
    faseKeys.forEach(faseKey => {
        const faseMateriais = fases[faseKey];

        if (faseKeys.length > 0) {
            worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
            worksheet.getCell(`A${currentRow}`).value = `FASE ${faseKey}`;
            worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
            worksheet.getCell(`A${currentRow}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ff5480ba' } };
            worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };
            for (let c = 1; c <= 7; c++) {
                worksheet.getCell(currentRow, c).border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
            }
            currentRow++;
        }

        faseMateriais.forEach(mp => {
            const pCell = worksheet.getCell(`A${currentRow}`);
            pCell.value = mp.quantity ? Number(parseFloat(mp.quantity)) : '';
            pCell.numFmt = '#,##0.000###';

            const codigoCell = worksheet.getCell(`B${currentRow}`);
            codigoCell.value = mp.code || '';
            codigoCell.font = { bold: true, size: 11 };
            codigoCell.alignment = { horizontal: 'center', vertical: 'middle' };

            worksheet.getCell(`C${currentRow}`).value = '';

            const nameCell = worksheet.getCell(`D${currentRow}`);
            let displayName = mp.commercialName || mp.inciName || '';
            if (mp.isSemiFinished) {
                displayName = `[SA] ${displayName}`;
            }
            nameCell.value = displayName;

            const unCell = worksheet.getCell(`E${currentRow}`);
            unCell.value = 'KG';
            unCell.alignment = { horizontal: 'center', vertical: 'middle' };

            const qCell = worksheet.getCell(`F${currentRow}`);
            qCell.value = mp.needed ? Number(mp.needed) : '';
            qCell.numFmt = '#,##0.0000######';

            // Lote Utilizado
            const loteCell = worksheet.getCell(`G${currentRow}`);
            loteCell.value = mp.lotNumber || '—';
            loteCell.font = { size: 10 };
            loteCell.alignment = { horizontal: 'center', vertical: 'middle' };

            for (let c = 1; c <= 7; c++) {
                const cell = worksheet.getCell(currentRow, c);
                cell.font = { size: 11 };
                if (c !== 2 && c !== 5 && c !== 7) {
                    cell.alignment = { horizontal: c === 6 ? 'right' : 'left', vertical: 'middle', wrapText: true };
                }
                cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
            }
            currentRow++;
        });
    });

    // TOTAL ROW
    const percentTotalCell = worksheet.getCell(`A${currentRow}`);
    percentTotalCell.value = { formula: `SUM(A${itemsStartRow}:A${currentRow - 1})` };
    percentTotalCell.font = { bold: true, size: 12 };
    percentTotalCell.numFmt = '#,##0.000###';
    percentTotalCell.alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.mergeCells(`B${currentRow}:D${currentRow}`);
    worksheet.getCell(`B${currentRow}`).value = 'TOTAL';
    worksheet.getCell(`B${currentRow}`).font = { bold: true, size: 12 };
    worksheet.getCell(`B${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.getCell(`E${currentRow}`).value = 'KG';
    worksheet.getCell(`E${currentRow}`).font = { bold: true, size: 12 };
    worksheet.getCell(`E${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };

    const totalCell = worksheet.getCell(`F${currentRow}`);
    totalCell.value = { formula: `SUM(F${itemsStartRow}:F${currentRow - 1})` };
    totalCell.font = { bold: true, size: 12 };
    totalCell.numFmt = '#,##0.0000######';
    totalCell.alignment = { horizontal: 'right', vertical: 'middle' };

    worksheet.getCell(`G${currentRow}`).value = '';
    worksheet.getCell(`G${currentRow}`).alignment = { horizontal: 'center', vertical: 'middle' };

    for (let c = 1; c <= 7; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Responsible for Weighing
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Responsável pela Pesagem:';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 10 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells(`E${currentRow}:G${currentRow}`);
    worksheet.getCell(`E${currentRow}`).value = `Data: ${formatDateBR(new Date())}`;
    worksheet.getCell(`E${currentRow}`).font = { bold: true, size: 10 };
    worksheet.getCell(`E${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    for (let c = 1; c <= 7; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Responsible for Manufacturing
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Responsável pela Fabricação: ';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 10 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells(`E${currentRow}:G${currentRow}`);
    worksheet.getCell(`E${currentRow}`).value = `Data: ${formatDateBR(new Date())}`;
    worksheet.getCell(`E${currentRow}`).font = { bold: true, size: 10 };
    worksheet.getCell(`E${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    for (let c = 1; c <= 7; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Tolerance section
    addSectionHeader(worksheet, currentRow, 'TOLERÂNCIA PARA PESAGEM', 7);
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Tolerância máxima permitida de pesagem: ±1%';
    worksheet.getCell(`A${currentRow}`).font = { size: 11 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    for (let c = 1; c <= 7; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
    }
    currentRow++;

    // Preparation method section
    addSectionHeader(worksheet, currentRow, 'MODO DE PREPARO', 7);
    currentRow++;

    if (preparationSteps && preparationSteps.length > 0) {
        const sortedSteps = [...preparationSteps].sort((a, b) => (a.order || 0) - (b.order || 0));
        sortedSteps.forEach((step, index) => {
            worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
            worksheet.getCell(`A${currentRow}`).value = `${index + 1}. ${step.description || ''}`;
            worksheet.getCell(`A${currentRow}`).font = { size: 10 };
            worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

            for (let c = 1; c <= 7; c++) {
                worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
            }

            const stepRow = worksheet.getRow(currentRow);
            stepRow.height = (step.description && step.description.length > 80) ? 35 : 20;
            currentRow++;
        });
    } else {
        for (let i = 1; i <= 5; i++) {
            worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
            worksheet.getCell(`A${currentRow}`).value = `${i}.`;
            worksheet.getCell(`A${currentRow}`).font = { size: 10 };
            worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
            worksheet.getRow(currentRow).height = 20;
            for (let c = 1; c <= 7; c++) {
                worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
            }
            currentRow++;
        }
    }

    // Observations (4 lines)
    for (let i = 1; i <= 4; i++) {
        worksheet.mergeCells(`A${currentRow}:G${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = i === 1 ? 'Observações: ' : '';
        worksheet.getCell(`A${currentRow}`).font = { size: 10 };
        worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
        worksheet.getRow(currentRow).height = 25;
        for (let c = 1; c <= 7; c++) {
            worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'medium' }, bottom: { style: 'thin' }, right: { style: 'medium' } };
        }
        currentRow++;
    }

    currentRow++;

    // Approval line
    const aprovRow = worksheet.getRow(currentRow);
    aprovRow.height = 45;

    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Elaborado por: Kátia Fernandes\nData: 17/06/2026';
    worksheet.getCell(`A${currentRow}`).font = { size: 9 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
    worksheet.getCell(`C${currentRow}`).value = 'Revisado por: Magali Murro\nData: 17/06/2026';
    worksheet.getCell(`C${currentRow}`).font = { size: 9 };
    worksheet.getCell(`C${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`E${currentRow}:G${currentRow}`);
    worksheet.getCell(`E${currentRow}`).value = 'Aprovado por: Nisrine El Hazzat\nData: 17/06/2026';
    worksheet.getCell(`E${currentRow}`).font = { size: 9 };
    worksheet.getCell(`E${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    for (let c = 1; c <= 7; c++) {
        worksheet.getCell(currentRow, c).border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    }

    worksheet.pageSetup.orientation = 'portrait';
    worksheet.pageSetup.fitToPage = true;
    worksheet.pageSetup.fitToWidth = 1;
    worksheet.pageSetup.fitToHeight = 1;
    worksheet.pageSetup.paperSize = 9;
};

// ============================================================
// ABA 2: FICHA DE PESAGEM / ENVASE
// ============================================================

const createPesagemEnvaseSheet = (workbook, logoImageId, params) => {
    const {
        item,
        productData,
        allRawMaterials,
        allCategories,
        allProductTypes,
        order
    } = params;

    const sheetName = sanitizeSheetName(`${item.productCode}_Pesagem_Envase`);
    const worksheet = workbook.addWorksheet(sheetName);

    // Column widths
    worksheet.getColumn(1).width = 35;
    worksheet.getColumn(2).width = 55;
    worksheet.getColumn(3).width = 20;
    worksheet.getColumn(4).width = 40;

    // Calculations
    const mode = item.mode || 'mass';
    const quantity = parseFloat(item.quantity) || 0;
    const lossIndex = parseFloat(productData.lossIndex) || 1;
    const gramatura = parseFloat(productData.gramatura) || 0;
    const gramaturaUnit = productData.gramaturaUnit || 'g';

    const gramaturaBase = (gramaturaUnit === 'g' || gramaturaUnit === 'ml')
        ? gramatura / 1000
        : gramatura;

    let pesoCalculado, pesoProgramado, perdaPercentual;

    if (mode === 'mass') {
        pesoCalculado = quantity;
        perdaPercentual = ((lossIndex - 1) * 100).toFixed(1);
        pesoProgramado = quantity * lossIndex;
    } else {
        pesoCalculado = quantity * gramaturaBase;
        perdaPercentual = ((lossIndex - 1) * 100).toFixed(1);
        pesoProgramado = pesoCalculado * lossIndex;
    }

    let currentRow = 1;

    // ROW 1: Logo + Title + Document Code
    const row1 = worksheet.getRow(currentRow);
    row1.height = 70;

    if (logoImageId != null) {
        worksheet.addImage(logoImageId, {
            tl: { col: 0.1, row: 0.050 },
            ext: { width: 230, height: 85 },
            editAs: 'oneCell'
        });
    }

    worksheet.mergeCells('B1:C1');
    worksheet.getCell('B1').value = 'FICHA DE PESAGEM\nQuantidade Efetiva de Produto Fabricado';
    worksheet.getCell('B1').font = { bold: true, size: 12 };
    worksheet.getCell('B1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    worksheet.getCell('D1').value = 'FQ 35\nRev. 02';
    worksheet.getCell('D1').font = { bold: true, size: 12 };
    worksheet.getCell('D1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 2;

    // Row 2: Product Name
    const row2 = worksheet.getRow(2);
    row2.height = 25;
    worksheet.mergeCells('A2:D2');
    worksheet.getCell('A2').value = `Produto: ${item.productCode} - ${item.productName}`;
    worksheet.getCell('A2').font = { bold: true, size: 12 };
    worksheet.getCell('A2').alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 3;

    // Row 3: LOTE and O.P.
    const row3 = worksheet.getRow(3);
    row3.height = 25;
    worksheet.getCell('A3').value = `LOTE: ${item.lote || '___________'}`;
    worksheet.getCell('A3').font = { bold: true, size: 11 };
    worksheet.getCell('A3').alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells('A3:B3');

    worksheet.getCell('C3').value = `O.P: ${order.opCode || '___________'}`;
    worksheet.getCell('C3').font = { bold: true, size: 11 };
    worksheet.getCell('C3').alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells('C3:D3');

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 4;

    // Row 4: Data de Fabricação
    const row4 = worksheet.getRow(4);
    row4.height = 25;
    worksheet.mergeCells('A4:D4');
    const fabDate = order.fabricationDate ? formatDateBR(order.fabricationDate) : '____/____/____';
    worksheet.getCell('A4').value = `DATA DE FABRICAÇÃO: ${fabDate}`;
    worksheet.getCell('A4').font = { bold: true, size: 11 };
    worksheet.getCell('A4').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 5;

    // Row 5: Data de Validade
    const row5 = worksheet.getRow(5);
    row5.height = 25;
    worksheet.mergeCells('A5:D5');
    const expDate = order.expirationDate ? formatDateBR(order.expirationDate) : '____/____/____';
    worksheet.getCell('A5').value = `DATA DE VALIDADE: ${expDate}`;
    worksheet.getCell('A5').font = { bold: true, size: 11 };
    worksheet.getCell('A5').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 6;

    // Row 6: INFORMAÇÕES PARA O ENVASE (title)
    addTitleRow(worksheet, currentRow, 'INFORMAÇÕES PARA O ENVASE:', 4);
    currentRow = 7;

    // Row 7: Quantidade Produzida, Perda, Quantidade Programada
    const row7 = worksheet.getRow(7);
    row7.height = 25;

    worksheet.getCell('A7').value = `QUANTIDADE PRODUZIDA: ${pesoCalculado.toFixed(3)} KG`;
    worksheet.getCell('A7').font = { bold: true, size: 11 };
    worksheet.getCell('A7').alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.getCell('B7').value = `PERDA: ${perdaPercentual}%`;
    worksheet.getCell('B7').font = { bold: true, size: 11 };
    worksheet.getCell('B7').alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.mergeCells('C7:D7');
    worksheet.getCell('C7').value = `QUANTIDADE PROGRAMADA: ${pesoProgramado.toFixed(3)} KG`;
    worksheet.getCell('C7').font = { bold: true, size: 11 };
    worksheet.getCell('C7').alignment = { horizontal: 'center', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 8;

    // Row 8: Capacidade do Frasco, Peso do Produto + Tara
    const row8 = worksheet.getRow(8);
    row8.height = 25;

    worksheet.mergeCells('A8:B8');
    const gramaturaDisplay = parseFloat(productData.gramatura) || 0;
    const gramaturaUnitDisplay = productData.gramaturaUnit || 'g';
    worksheet.getCell('A8').value = `CAPACIDADE DO FRASCO: ${gramaturaDisplay} ${gramaturaUnitDisplay.toUpperCase()}`;
    worksheet.getCell('A8').font = { bold: true, size: 11 };
    worksheet.getCell('A8').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('C8:D8');
    worksheet.getCell('C8').value = 'PESO DO PRODUTO + TARA: ';
    worksheet.getCell('C8').font = { bold: true, size: 11 };
    worksheet.getCell('C8').alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 9;

    // Row 9: Tanque, Tara da Embalagem
    const row9 = worksheet.getRow(9);
    row9.height = 25;

    worksheet.mergeCells('A9:B9');
    worksheet.getCell('A9').value = 'TANQUE: ';
    worksheet.getCell('A9').font = { bold: true, size: 11 };
    worksheet.getCell('A9').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('C9:D9');
    worksheet.getCell('C9').value = 'TARA DA EMBALAGEM: ';
    worksheet.getCell('C9').font = { bold: true, size: 11 };
    worksheet.getCell('C9').alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 10;

    // Row 10: Quantidade Total de Embalagens
    const row10 = worksheet.getRow(10);
    row10.height = 25;
    worksheet.mergeCells('A10:D10');
    let qtdeTotalEmbalagens = '';
    if (gramaturaBase > 0) {
        qtdeTotalEmbalagens = Math.ceil(pesoCalculado / gramaturaBase);
        worksheet.getCell('A10').value = `QUANTIDADE TOTAL DE EMBALAGENS: ${qtdeTotalEmbalagens} UNIDADE(S)`;
    } else {
        worksheet.getCell('A10').value = `QUANTIDADE TOTAL DE EMBALAGENS: __________ UNIDADE(S)`;
    }

    worksheet.getCell('A10').font = { bold: true, size: 11 };
    worksheet.getCell('A10').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 11;

    // Row 11: Frascos/Acessórios para Embalagem
    const row11 = worksheet.getRow(11);
    row11.height = 25;
    worksheet.mergeCells('A11:D11');

    let packagingDescription = 'FRASCOS/ACESSÓRIOS PARA EMBALAGEM: ';

    const embalagemCategory = allCategories?.find(
        cat => cat.name?.toUpperCase() === 'EMBALAGENS'
    );
    const serigrafiaType = allProductTypes?.find(
        type => type.name?.toUpperCase() === 'SERIGRAFIA'
    );

    const packagingMaterials = (productData.rawMaterials || [])
        .map(mp => {
            const rawMaterial = allRawMaterials.find(rm => rm.id === mp.id);
            return { rawMaterial, quantity: mp.quantity };
        })
        .filter(item => {
            if (!item.rawMaterial) return false;

            const rm = item.rawMaterial;
            const code = (rm.code || "").toUpperCase();

            const isEmbalagem = embalagemCategory &&
                rm.categoryIds?.includes(embalagemCategory.id);

            if (!isEmbalagem) return false;

            const isSerigrafiaType = serigrafiaType &&
                rm.productTypeIds?.includes(serigrafiaType.id);
            const hasSerCode = code.includes("SER");

            return !isSerigrafiaType && !hasSerCode;
        });

    if (packagingMaterials.length > 0) {
        const packagingList = packagingMaterials.map(item => {
            const rm = item.rawMaterial;
            const code = rm.code || '';
            const name = rm.commercialName || rm.inciName || rm.name || '';
            return `${code} - ${name}`;
        });
        packagingDescription += packagingList.join(' | ');
    }

    worksheet.getCell('A11').value = packagingDescription;
    worksheet.getCell('A11').font = { bold: true, size: 11 };
    worksheet.getCell('A11').alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 12;

    // Row 12: LIBERAÇÃO DO CONTROLE DE QUALIDADE PARA O ENVASE
    addTitleRow(worksheet, currentRow, 'LIBERAÇÃO DO CONTROLE DE QUALIDADE PARA O ENVASE', 4);
    currentRow = 13;

    // Row 13: Responsável, Data, Horário
    const row13 = worksheet.getRow(13);
    row13.height = 30;

    worksheet.getCell('A13').value = 'RESPONSÁVEL: ';
    worksheet.getCell('A13').font = { bold: true, size: 11 };
    worksheet.getCell('A13').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.getCell('B13').value = `DATA: ${formatDateBR(new Date())}`;
    worksheet.getCell('B13').font = { bold: true, size: 11 };
    worksheet.getCell('B13').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('C13:D13');
    worksheet.getCell('C13').value = 'HORÁRIO: ____:____';
    worksheet.getCell('C13').font = { bold: true, size: 11 };
    worksheet.getCell('C13').alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 14;

    // Row 14: ENVASE (title)
    addTitleRow(worksheet, currentRow, 'ENVASE', 4);
    currentRow = 15;

    // Row 15: Envasadora Usada
    const row15 = worksheet.getRow(15);
    row15.height = 25;
    worksheet.mergeCells('A15:D15');
    worksheet.getCell('A15').value = `ENVASADORA USADA: ${order.phase8Data?.maquinaEnvase || ''}`;
    worksheet.getCell('A15').font = { bold: true, size: 11 };
    worksheet.getCell('A15').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 16;

    // Rows 16-20: ENVASE vertical title + details
    const envaseTitleRow = 16;
    const envaseEndRow = 20;

    worksheet.mergeCells(envaseTitleRow, 1, envaseEndRow, 1);
    const envaseVerticalTitle = worksheet.getCell('A16');
    envaseVerticalTitle.value = 'ENVASE';
    envaseVerticalTitle.font = { bold: true, size: 11 };
    envaseVerticalTitle.alignment = { horizontal: 'center', vertical: 'middle', textRotation: 0 };

    worksheet.mergeCells('B16:C16');
    const cell16Left = worksheet.getCell('B16');
    cell16Left.value = 'DATA DE INICIO: ____/____/____';
    cell16Left.font = { size: 11 };
    cell16Left.alignment = { horizontal: 'left', vertical: 'middle' };

    const cell16Right = worksheet.getCell('D16');
    cell16Right.value = 'HORÁRIO DE INICIO: ____:____';
    cell16Right.font = { size: 11 };
    cell16Right.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B17:C17');
    const cell17Left = worksheet.getCell('B17');
    cell17Left.value = 'DATA DE TÉRMINO: ____/____/____';
    cell17Left.font = { size: 11 };
    cell17Left.alignment = { horizontal: 'left', vertical: 'middle' };

    const cell17Right = worksheet.getCell('D17');
    cell17Right.value = 'HORÁRIO DE TÉRMINO: ____:____';
    cell17Right.font = { size: 11 };
    cell17Right.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B18:D18');
    const cell18 = worksheet.getCell('B18');
    cell18.value = 'RESPONSÁVEL PELO ENVASE: ';
    cell18.font = { size: 11 };
    cell18.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B19:D19');
    const cell19 = worksheet.getCell('B19');
    cell19.value = 'AUXILIARES:  ';
    cell19.font = { size: 11 };
    cell19.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B20:D20');
    const cell20 = worksheet.getCell('B20');
    cell20.value = '';
    cell20.font = { size: 11 };

    for (let r = envaseTitleRow; r <= envaseEndRow; r++) {
        applyRowBorder(worksheet, r, 4);
        worksheet.getRow(r).height = 25;
    }
    currentRow = 21;

    // Rows 21-25: DATAÇÃO vertical title + details
    const datacaoTitleRow = 21;
    const datacaoEndRow = 25;

    worksheet.mergeCells(datacaoTitleRow, 1, datacaoEndRow, 1);
    const datacaoVerticalTitle = worksheet.getCell('A21');
    datacaoVerticalTitle.value = 'DATAÇÃO';
    datacaoVerticalTitle.font = { bold: true, size: 11 };
    datacaoVerticalTitle.alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.mergeCells('B21:D21');
    const cell21 = worksheet.getCell('B21');
    cell21.value = 'RESPONSÁVEL PELA DATAÇÃO: ';
    cell21.font = { size: 11 };
    cell21.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B22:D22');
    const cell22 = worksheet.getCell('B22');
    cell22.value = 'CONFERENTE DA DATAÇÃO: ';
    cell22.font = { size: 11 };
    cell22.alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('B23:C25');
    const cell23Left = worksheet.getCell('B23');
    cell23Left.value = 'DATAÇÃO INICIAL';
    cell23Left.font = { bold: true, size: 11 };
    cell23Left.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
    cell23Left.alignment = { horizontal: 'center', vertical: 'top', wrapText: true };

    worksheet.mergeCells('D23:D25');
    const cell24Right = worksheet.getCell('D23');
    cell24Right.value = 'DATAÇÃO FINAL';
    cell24Right.font = { bold: true, size: 11 };
    cell24Right.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
    cell24Right.alignment = { horizontal: 'center', vertical: 'top', wrapText: true };

    for (let r = 21; r <= 25; r++) {
        applyRowBorder(worksheet, r, 4);
        worksheet.getRow(r).height = 25;
    }
    currentRow = 26;

    // Row 26: Quantidade de Embalagens Envasadas
    const row26 = worksheet.getRow(26);
    row26.height = 25;
    worksheet.mergeCells('A26:D26');
    worksheet.getCell('A26').value = `QUANTIDADE DE EMBALAGENS ENVASADAS: ${order.phase10Data?.quantidadeEmbalada || ''}`;
    worksheet.getCell('A26').font = { bold: true, size: 11 };
    worksheet.getCell('A26').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 27;

    // Row 27: Quantidade para a Quarentena
    const row27 = worksheet.getRow(27);
    row27.height = 25;
    worksheet.mergeCells('A27:D27');
    worksheet.getCell('A27').value = 'QUANTIDADE PARA A QUARENTENA: ';
    worksheet.getCell('A27').font = { bold: true, size: 11 };
    worksheet.getCell('A27').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow = 28;

    // Row 28: OBSERVAÇÃO DO PROCESSO DE ENVASE
    addTitleRow(worksheet, currentRow, 'OBSERVAÇÃO DO PROCESSO DE ENVASE', 4);
    currentRow = 29;

    const envaseObs = order.phase9Data?.observations || '';
    const obsLines = envaseObs.split('\n');
    for (let i = 0; i < Math.max(obsLines.length, 4); i++) {
        const row = worksheet.getRow(currentRow);
        row.height = 25;
        worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = obsLines[i] || '';
        worksheet.getCell(`A${currentRow}`).font = { size: 11 };
        applyRowBorder(worksheet, currentRow, 4);
        currentRow++;
    }

    // PERDA FINAL EM (%) DE PRODUTO
    addTitleRow(worksheet, currentRow, 'PERDA FINAL EM (%) DE PRODUTO', 4);
    currentRow++;

    const rowPerda = worksheet.getRow(currentRow);
    rowPerda.height = 25;
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = `% ${order.phase10Data?.perdaFinal || ''}`;
    worksheet.getCell(`A${currentRow}`).font = { size: 11 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4);
    currentRow++;

    // Visto do CQ
    const rowVisto = worksheet.getRow(currentRow);
    rowVisto.height = 30;

    worksheet.mergeCells(`A${currentRow}:B${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'VISTO DO CQ: ';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 11 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
    worksheet.getCell(`C${currentRow}`).value = `DATA: ${formatDateBR(new Date())}`;
    worksheet.getCell(`C${currentRow}`).font = { bold: true, size: 11 };
    worksheet.getCell(`C${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4);
    currentRow++;

    // INSPEÇÃO FINAL
    addTitleRow(worksheet, currentRow, 'INSPEÇÃO FINAL', 4);
    currentRow++;

    for (let i = 0; i < 3; i++) {
        const row = worksheet.getRow(currentRow);
        row.height = 25;
        worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = '';
        worksheet.getCell(`A${currentRow}`).font = { size: 11 };
        applyRowBorder(worksheet, currentRow, 4);
        currentRow++;
    }

    currentRow++;

    const aprovRow = worksheet.getRow(currentRow);
    aprovRow.height = 45;

    worksheet.getCell(`A${currentRow}`).value = 'Elaborado por: Kátia Fernandes\nData: 11/05/2026';
    worksheet.getCell(`A${currentRow}`).font = { size: 9 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.getCell(`B${currentRow}`).value = 'Revisado por: Magali Murro\nData: 11/05/2026';
    worksheet.getCell(`B${currentRow}`).font = { size: 9 };
    worksheet.getCell(`B${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
    worksheet.getCell(`C${currentRow}`).value = 'Aprovado por: Nisrine El Hazzat\nData: 11/05/2026';
    worksheet.getCell(`C${currentRow}`).font = { size: 9 };
    worksheet.getCell(`C${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    for (let c = 1; c <= 4; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    }

    worksheet.pageSetup.orientation = 'portrait';
    worksheet.pageSetup.fitToPage = true;
    worksheet.pageSetup.fitToWidth = 1;
    worksheet.pageSetup.fitToHeight = 1;
    worksheet.pageSetup.paperSize = 9;
};

// ============================================================
// ABA 3: CONTROLE DE QUALIDADE (COM COLUNA DE RESULTADOS OBTIDOS)
// ============================================================

const createControleQualidadeSheet = (workbook, logoImageId, params) => {
    const {
        item,
        productData,
        order
    } = params;

    const sheetName = sanitizeSheetName(`${item.productCode}_CQ`);
    const worksheet = workbook.addWorksheet(sheetName);

    // Column widths
    worksheet.getColumn(1).width = 35;  // ANÁLISES
    worksheet.getColumn(2).width = 45;  // VALORES PADRÕES
    worksheet.getColumn(3).width = 30;  // RESULTADO (Status)
    worksheet.getColumn(4).width = 40;  // VALOR OBTIDO (NOVO - resultado específico)

    let currentRow = 1;

    // ROW 1: Logo + Title + Document Code
    const row1 = worksheet.getRow(currentRow);
    row1.height = 70;

    if (logoImageId != null) {
        worksheet.addImage(logoImageId, {
            tl: { col: 0.1, row: 0.050 },
            ext: { width: 230, height: 85 },
            editAs: 'oneCell'
        });
    }

    worksheet.getCell('B1').value = 'PROCESSO DO CONTROLE DE QUALIDADE\nANÁLISE E ACOMPANHAMENTO DE PRODUTO ACABADO';
    worksheet.getCell('B1').font = { bold: true, size: 14 };
    worksheet.getCell('B1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    worksheet.getCell('C1').value = 'FQ 22\nRev. 02';
    worksheet.getCell('C1').font = { bold: true, size: 12 };
    worksheet.getCell('C1').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

    worksheet.mergeCells('D1:D1');
    worksheet.getCell('D1').value = '';
    worksheet.getCell('D1').alignment = { horizontal: 'center', vertical: 'middle' };

    ['A', 'B', 'C', 'D'].forEach(col => {
        worksheet.getCell(`${col}1`).border = {
            top: { style: 'medium' },
            left: { style: col === 'A' ? 'medium' : 'thin' },
            bottom: { style: 'thin' },
            right: { style: col === 'D' ? 'medium' : 'thin' }
        };
    });

    currentRow = 2;

    // DADOS DO PRODUTO
    addTitleRow(worksheet, currentRow, 'DADOS DO PRODUTO', 4);
    currentRow = 3;

    // PRODUTO
    const row3 = worksheet.getRow(3);
    row3.height = 25;
    worksheet.mergeCells('A3:D3');
    worksheet.getCell('A3').value = `PRODUTO: ${item.productCode} - ${item.productName}`;
    worksheet.getCell('A3').font = { bold: true, size: 11 };
    worksheet.getCell('A3').alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    applyRowBorder(worksheet, currentRow, 4, 'thin');
    currentRow = 4;

    // DATA DE FABRICAÇÃO e DATA DE VALIDADE
    const row4 = worksheet.getRow(4);
    row4.height = 25;
    worksheet.mergeCells('A4:B4');
    const fabDate = order.fabricationDate ? formatDateBR(order.fabricationDate) : '____/____/____';
    worksheet.getCell('A4').value = `DATA DE FABRICAÇÃO: ${fabDate}`;
    worksheet.getCell('A4').font = { size: 11 };
    worksheet.getCell('A4').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4, 'thin');

    worksheet.mergeCells('C4:D4');
    const expDate = order.expirationDate ? formatDateBR(order.expirationDate) : '____/____/____';
    worksheet.getCell('C4').value = `DATA DE VALIDADE: ${expDate}`;
    worksheet.getCell('C4').font = { size: 11 };
    worksheet.getCell('C4').alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4, 'thin');
    currentRow = 5;

    // LOTE e O.P
    const row5 = worksheet.getRow(5);
    row5.height = 25;
    worksheet.mergeCells('A5:B5');
    worksheet.getCell('A5').value = `LOTE: ${item.lote || '_______________'}`;
    worksheet.getCell('A5').font = { bold: true, size: 11 };
    worksheet.getCell('A5').alignment = { horizontal: 'left', vertical: 'middle' };

    worksheet.mergeCells('C5:D5');
    worksheet.getCell('C5').value = `O.P: ${order.opCode || ''}`;
    worksheet.getCell('C5').font = { bold: true, size: 11 };
    worksheet.getCell('C5').alignment = { horizontal: 'left', vertical: 'middle' };

    applyRowBorder(worksheet, currentRow, 4, 'thin');
    currentRow = 6;

    // ANÁLISES ORGANOLÉPTICAS E FÍSICO-QUÍMICAS NA FABRICAÇÃO
    addTitleRow(worksheet, currentRow, 'ANÁLISES ORGANOLÉPTICAS E FÍSICO-QUÍMICAS NA FABRICAÇÃO', 4);
    currentRow = 7;

    // Table headers
    const row7 = worksheet.getRow(7);
    row7.height = 30;

    const headers = ['ANÁLISES', 'VALORES PADRÕES', 'RESULTADO', 'VALOR OBTIDO'];
    headers.forEach((header, i) => {
        const cell = worksheet.getCell(7, i + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FF2E4057' }, size: 10 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    });
    applyRowBorder(worksheet, currentRow, 4, 'thin');
    currentRow = 8;

    // Analysis criteria
    const analyses = [
        { name: 'Cor', field: 'cor' },
        { name: 'Odor', field: 'odor' },
        { name: 'pH', field: 'ph' },
        { name: 'Viscosidade', field: 'viscosidade' },
        { name: 'Centrífuga', field: 'centrifuga' }
    ];

    const qualityCriteria = productData.qualityCriteria || [];
    const qualityResults = order.qualityResults || {};

    analyses.forEach((analysis) => {
        const row = worksheet.getRow(currentRow);
        row.height = 20;

        const criteria = qualityCriteria.find(qc =>
            qc.analysis?.toLowerCase() === analysis.name.toLowerCase()
        );
        const padrao = criteria?.specification || '';

        // Busca o resultado se disponível
        const result = qualityResults[criteria?.id] || {};

        // Status
        const statusMap = {
            'approved': '✅ Aprovado',
            'rejected': '❌ Rejeitado',
            'restricted': '⚠️ Com restrições',
            'pending': '⏳ Pendente'
        };
        const status = statusMap[result.status] || '⏳ Pendente';

        // Valor Obtido (resultado específico)
        const valorObtido = result.valorObtido || result.observation || '';

        worksheet.getCell(currentRow, 1).value = analysis.name;
        worksheet.getCell(currentRow, 1).font = { bold: true, size: 10 };
        worksheet.getCell(currentRow, 1).alignment = { horizontal: 'left', vertical: 'middle' };

        worksheet.getCell(currentRow, 2).value = padrao;
        worksheet.getCell(currentRow, 2).font = { size: 10 };
        worksheet.getCell(currentRow, 2).alignment = { horizontal: 'left', vertical: 'middle' };

        worksheet.getCell(currentRow, 3).value = status;
        worksheet.getCell(currentRow, 3).font = { size: 10 };
        worksheet.getCell(currentRow, 3).alignment = { horizontal: 'center', vertical: 'middle' };

        worksheet.getCell(currentRow, 4).value = valorObtido;
        worksheet.getCell(currentRow, 4).font = { size: 10 };
        worksheet.getCell(currentRow, 4).alignment = { horizontal: 'left', vertical: 'middle' };

        applyRowBorder(worksheet, currentRow, 4, 'thin');
        currentRow++;
    });

    // VISTO DO CONTROLE DE QUALIDADE
    const rowVisto = worksheet.getRow(currentRow);
    rowVisto.height = 30;
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'VISTO DO CONTROLE DE QUALIDADE: ';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 11 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4, 'thin');
    currentRow++;

    // DATA DA LIBERAÇÃO PARA ENVASE
    const rowLiberacao = worksheet.getRow(currentRow);
    rowLiberacao.height = 25;
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    const releaseDate = order.phase6Data?.releasedAt ? new Date(order.phase6Data.releasedAt).toLocaleDateString('pt-BR') : '____/____/____';
    worksheet.getCell(`A${currentRow}`).value = `DATA DA LIBERAÇÃO PARA ENVASE: ${releaseDate}`;
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 11 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4, 'thin');
    currentRow++;

    // OBSERVAÇÕES
    const rowObs = worksheet.getRow(currentRow);
    rowObs.height = 25;
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    const obsText = order.phase6Data?.observations || order.phase10Data?.observacoesQualidade || '';
    worksheet.getCell(`A${currentRow}`).value = `OBSERVAÇÕES: ${obsText}`;
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 11 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle' };
    applyRowBorder(worksheet, currentRow, 4, 'thin');
    currentRow++;

    // Empty observation line
    const rowObsEmpty = worksheet.getRow(currentRow);
    rowObsEmpty.height = 25;
    worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = '';
    applyRowBorder(worksheet, currentRow, 4, 'thin');
    currentRow++;

    // ACOMPANHAMENTOS FÍSICO-QUÍMICOS E ORGANOLÉPTICOS NA QUARENTENA
    addTitleRow(worksheet, currentRow, 'ACOMPANHAMENTOS FÍSICO-QUÍMICOS E ORGANOLÉPTICOS NA QUARENTENA', 4);
    currentRow++;

    // Function to create quarantine follow-up blocks
    const createQuarantineBlock = (startRow, titleText) => {
        // Header row
        const headerRow = worksheet.getRow(startRow);
        headerRow.height = 25;

        worksheet.getCell(startRow, 1).value = 'ANÁLISES';
        worksheet.getCell(startRow, 1).font = { bold: true, color: { argb: 'FF2E4057' }, size: 10 };
        worksheet.getCell(startRow, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        worksheet.getCell(startRow, 1).alignment = { horizontal: 'center', vertical: 'middle' };

        worksheet.getCell(startRow, 2).value = titleText;
        worksheet.getCell(startRow, 2).font = { bold: true, color: { argb: 'FF2E4057' }, size: 10 };
        worksheet.getCell(startRow, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        worksheet.getCell(startRow, 2).alignment = { horizontal: 'center', vertical: 'middle' };

        worksheet.getCell(startRow, 3).value = 'DATA';
        worksheet.getCell(startRow, 3).font = { bold: true, color: { argb: 'FF2E4057' }, size: 10 };
        worksheet.getCell(startRow, 3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        worksheet.getCell(startRow, 3).alignment = { horizontal: 'center', vertical: 'middle' };

        worksheet.getCell(startRow, 4).value = 'RESULTADO OBTIDO';
        worksheet.getCell(startRow, 4).font = { bold: true, color: { argb: 'FF2E4057' }, size: 10 };
        worksheet.getCell(startRow, 4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8EDF2' } };
        worksheet.getCell(startRow, 4).alignment = { horizontal: 'center', vertical: 'middle' };

        applyRowBorder(worksheet, startRow, 4, 'medium');

        let row = startRow + 1;

        const quarantineAnalyses = ['Cor', 'Odor', 'pH', 'Viscosidade', 'Centrífuga'];
        quarantineAnalyses.forEach(analysis => {
            worksheet.getRow(row).height = 20;
            worksheet.getCell(row, 1).value = analysis;
            worksheet.getCell(row, 1).font = { bold: true, size: 10 };
            worksheet.getCell(row, 1).alignment = { horizontal: 'left', vertical: 'middle' };

            worksheet.getCell(row, 2).value = '';
            worksheet.getCell(row, 2).font = { size: 10 };
            worksheet.getCell(row, 2).alignment = { horizontal: 'left', vertical: 'middle' };

            worksheet.getCell(row, 3).value = '';
            worksheet.getCell(row, 3).font = { size: 10 };
            worksheet.getCell(row, 3).alignment = { horizontal: 'center', vertical: 'middle' };

            worksheet.getCell(row, 4).value = '';
            worksheet.getCell(row, 4).font = { size: 10 };
            worksheet.getCell(row, 4).alignment = { horizontal: 'left', vertical: 'middle' };

            applyRowBorder(worksheet, row, 4, 'thin');
            row++;
        });

        // Visto row
        worksheet.getRow(row).height = 30;
        worksheet.mergeCells(row, 1, row, 4);
        worksheet.getCell(row, 1).value = 'VISTO DO CONTROLE DE QUALIDADE: ';
        worksheet.getCell(row, 1).font = { bold: true, size: 11 };
        worksheet.getCell(row, 1).alignment = { horizontal: 'left', vertical: 'middle' };
        applyRowBorder(worksheet, row, 4, 'thin');
        row++;

        // Observações row
        worksheet.getRow(row).height = 25;
        worksheet.mergeCells(row, 1, row, 4);
        worksheet.getCell(row, 1).value = 'OBSERVAÇÕES:';
        worksheet.getCell(row, 1).font = { bold: true, size: 11 };
        worksheet.getCell(row, 1).alignment = { horizontal: 'left', vertical: 'middle' };
        applyRowBorder(worksheet, row, 4, 'thin');
        row++;

        // Empty observation line
        worksheet.getRow(row).height = 25;
        worksheet.mergeCells(row, 1, row, 4);
        worksheet.getCell(row, 1).value = '';
        applyRowBorder(worksheet, row, 4, 'thin');
        row++;

        return row;
    };

    // Quarantine blocks
    currentRow = createQuarantineBlock(currentRow, 'DADOS EM 30 DIAS');
    currentRow = createQuarantineBlock(currentRow, 'DADOS EM 60 DIAS');
    currentRow = createQuarantineBlock(currentRow, 'DADOS EM 90 DIAS');

    currentRow++;

    // Approval row
    const aprovRow = worksheet.getRow(currentRow);
    aprovRow.height = 45;

    worksheet.mergeCells(`A${currentRow}:A${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'Elaborado por: Kátia Fernandes\nData: 11/05/2026';
    worksheet.getCell(`A${currentRow}`).font = { size: 9 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`B${currentRow}:B${currentRow}`);
    worksheet.getCell(`B${currentRow}`).value = 'Revisado por: Magali Murro\nData: 11/05/2026';
    worksheet.getCell(`B${currentRow}`).font = { size: 9 };
    worksheet.getCell(`B${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
    worksheet.getCell(`C${currentRow}`).value = 'Aprovado por: Nisrine El Hazzat\nData: 11/05/2026';
    worksheet.getCell(`C${currentRow}`).font = { size: 9 };
    worksheet.getCell(`C${currentRow}`).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };

    for (let c = 1; c <= 4; c++) {
        worksheet.getCell(currentRow, c).border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
        };
    }

    worksheet.pageSetup.orientation = 'portrait';
    worksheet.pageSetup.fitToPage = true;
    worksheet.pageSetup.fitToWidth = 1;
    worksheet.pageSetup.fitToHeight = 1;
    worksheet.pageSetup.paperSize = 9;
};

// ============================================================
// FUNÇÃO PRINCIPAL: exportProductionDocument
// ============================================================

export const exportProductionDocument = async ({
    order,
    allRawMaterials = [],
    allCategories = [],
    allItems = [],
    allProducts = [],
    allProductTypes = [],
    showToast
}) => {
    try {
        if (!order || !order.items || order.items.length === 0) {
            if (showToast) showToast("Ordem sem produtos para gerar o documento.", 'warning');
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const logoImageId = await loadLogo(workbook);

        for (let itemIndex = 0; itemIndex < order.items.length; itemIndex++) {
            const item = order.items[itemIndex];
            let productData;

            try {
                const productSnap = await getDoc(doc(db, PRODUCTS_COLLECTION, item.productId));
                if (!productSnap.exists()) {
                    console.warn(`Product ${item.productCode} not found in Firestore, skipping...`);
                    continue;
                }
                productData = productSnap.data();
            } catch (fetchError) {
                console.error('Error fetching product from Firestore:', fetchError);
                if (showToast) {
                    showToast(`Erro ao buscar dados do produto ${item.productCode}: ${fetchError.message}`, 'error');
                }
                continue;
            }

            const params = {
                item,
                productData,
                allRawMaterials,
                allCategories,
                allItems,
                allProducts,
                allProductTypes,
                order,
                manipulationData: order.manipulationData || null
            };

            createManipulacaoSheet(workbook, logoImageId, params);
            createPesagemEnvaseSheet(workbook, logoImageId, params);
            createControleQualidadeSheet(workbook, logoImageId, params);
        }

        if (workbook.worksheets.length === 0) {
            if (showToast) showToast("Não foi possível gerar o documento.", 'error');
            return;
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const fileName = `Documento_Producao_${order.opCode || 'OP'}_${new Date().getTime()}.xlsx`;
        saveAs(blob, fileName);

        if (showToast) {
            showToast(`📄 Documento único gerado com sucesso para ${order.items.length} produto(s)!`, 'success', 5000);
        }

        return true;

    } catch (error) {
        console.error("Erro ao gerar documento único de produção:", error);
        if (showToast) {
            showToast(`Erro ao gerar documento: ${error.message}`, 'error', 0);
        }
        return false;
    }
};